import { useCallback, useEffect, useRef, useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AgGridReact } from "ag-grid-react";
import type {
  RowClickedEvent,
  GetContextMenuItemsParams,
  DefaultMenuItem,
  MenuItemDef,
  GridApi,
  RowClassParams,
} from "ag-grid-enterprise";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import { StatusIndicatorColors, TreePanelColors } from "../../../shared/theme";
import type { CasterTreeRow } from "../models";
import { useCasterTreeTableData } from "../hooks/useCasterTreeTableData";
import { useCasterContextMenu } from "../hooks/useCasterContextMenu";

const gridDefaultColDef = { ...defaultColDef, sortable: false };

interface Props {
  treeData: CasterTreeRow[];
  selectedRow: CasterTreeRow | null;
  isLoading: boolean;
  onRowSelect: (row: CasterTreeRow | null) => void;
  onSetOnline: (row: CasterTreeRow) => void;
  onSetOffline: (row: CasterTreeRow) => void;
  onInstall: (row: CasterTreeRow) => void;
  onUninstall: (row: CasterTreeRow) => void;
  onRepair: (row: CasterTreeRow) => void;
  onNewSparePart: (row: CasterTreeRow) => void;
  onEditPart: (row: CasterTreeRow) => void;
  onSetInitialMetric: (row: CasterTreeRow) => void;
  onCorrectDates: (row: CasterTreeRow) => void;
  onDeleteSparePart: (row: CasterTreeRow) => void;
}

const getDataPath = (data: CasterTreeRow) => data.path;
const getRowId = (params: { data: CasterTreeRow }) => params.data.id;

export const CasterTreePanel = ({
  treeData,
  selectedRow,
  isLoading,
  onRowSelect,
  onSetOnline,
  onSetOffline,
  onInstall,
  onUninstall,
  onRepair,
  onNewSparePart,
  onEditPart,
  onSetInitialMetric,
  onCorrectDates,
  onDeleteSparePart,
}: Props) => {
  const theme = useTheme();
  const gridApiRef = useRef<GridApi<CasterTreeRow> | null>(null);

  // Track previous treeData to detect actual changes
  const prevTreeDataRef = useRef<CasterTreeRow[]>([]);

  // Keep selectedRow in a ref so it's available in onGridReady
  const selectedRowRef = useRef<CasterTreeRow | null>(selectedRow);
  selectedRowRef.current = selectedRow;

  // Helper function to expand tree and select a row
  const expandAndSelectRow = useCallback(
    (api: GridApi<CasterTreeRow>, row: CasterTreeRow) => {
      const rowNode = api.getRowNode(row.id);

      if (rowNode) {
        // Expand all parent groups to make the row visible
        let parent = rowNode.parent;
        while (parent) {
          if (parent.group) {
            parent.setExpanded(true);
          }
          parent = parent.parent;
        }

        // Select the row
        rowNode.setSelected(true, true);
      }
    },
    [],
  );

  // Force complete data refresh when treeData changes
  useEffect(() => {
    if (gridApiRef.current && treeData !== prevTreeDataRef.current) {
      prevTreeDataRef.current = treeData;
      gridApiRef.current.updateGridOptions({ rowData: treeData });
      gridApiRef.current.redrawRows();
    }
  }, [treeData]);

  // Expand tree and select row when selectedRow changes (e.g., from 3D view)
  useEffect(() => {
    if (!gridApiRef.current || !selectedRow) return;
    expandAndSelectRow(gridApiRef.current, selectedRow);
  }, [selectedRow, expandAndSelectRow]);

  const { getMenuOptions } = useCasterContextMenu({
    onInstall,
    onUninstall,
    onRepair,
    onNewSparePart,
    onEditPart,
    onSetInitialMetric,
    onCorrectDates,
    onSetOnline,
    onSetOffline,
  });

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  const { columnDefs, autoGroupColumnDef } = useCasterTreeTableData({
    onSetOnline,
    onSetOffline,
  });

  // Row style for Caster-Offline rows (soft gray background)
  const getRowStyle = useMemo(
    () => (params: RowClassParams<CasterTreeRow>) => {
      const row = params.data;
      if (!row) return undefined;

      // Check if this row is under Caster-Offline (path includes "Offline")
      const isOfflineRow = row.path.some((segment) =>
        segment.toLowerCase().includes("offline"),
      );

      if (isOfflineRow) {
        return {
          backgroundColor: TreePanelColors.OFFLINE_ROW_BACKGROUND,
        };
      }
      return undefined;
    },
    [],
  );

  // Use a ref to keep getMenuOptions up-to-date without triggering re-registration
  const getMenuOptionsRef = useRef(getMenuOptions);
  getMenuOptionsRef.current = getMenuOptions;

  // Register context menu only once on mount
  useEffect(() => {
    registerConfig({
      id: "casterMoldContext",
      getOptions: (data: unknown) => {
        // Type guard: ensure data is a valid CasterTreeRow before processing
        if (!data || typeof data !== "object" || !("id" in data)) {
          return [];
        }
        return getMenuOptionsRef.current(data as CasterTreeRow);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<CasterTreeRow>) => {
      const row = event.data;
      if (row) {
        onRowSelect(row);
      }
    },
    [onRowSelect],
  );

  const getContextMenuItems = useCallback(
    (
      params: GetContextMenuItemsParams<CasterTreeRow>,
    ): (DefaultMenuItem | MenuItemDef)[] => {
      const row = params.node?.data;

      if (row) {
        params.api.deselectAll();
        params.node?.setSelected(true);
        onRowSelect(row);
      }

      showContextMenu(
        params.event as unknown as React.MouseEvent,
        row,
        "casterMoldContext",
      );

      return [];
    },
    [onRowSelect, showContextMenu],
  );

  const handleGridReady = useCallback(
    (params: { api: GridApi<CasterTreeRow> }) => {
      gridApiRef.current = params.api;

      // If there's a selected row when grid becomes ready, expand and select it
      if (selectedRowRef.current) {
        expandAndSelectRow(params.api, selectedRowRef.current);
      }
    },
    [expandAndSelectRow],
  );

  return (
    <Paper elevation={2}>
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: StatusIndicatorColors.OK,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                OK
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: StatusIndicatorColors.EMPTY,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Empty
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: StatusIndicatorColors.WARNING,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Warning
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: StatusIndicatorColors.ALERT,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Alert
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            height: "calc(100vh - 202px)",
            minHeight: "400px",
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
          }}
        >
          <AgGridReact<CasterTreeRow>
            rowData={treeData}
            columnDefs={columnDefs}
            defaultColDef={gridDefaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            treeData={true}
            getDataPath={getDataPath}
            getRowId={getRowId}
            getRowStyle={getRowStyle}
            groupDefaultExpanded={1}
            onGridReady={handleGridReady}
            onRowClicked={handleRowClicked}
            getContextMenuItems={getContextMenuItems}
            loadingOverlayComponent={CenteredLazyLoading}
            loading={isLoading}
            {...defaultGridOptions}
            suppressContextMenu={false}
          />
        </Box>
      </Box>
    </Paper>
  );
};
